import React, { FC, ReactNode, useMemo } from "react";
import { useAtom } from "jotai/index";
import classNames from "classnames";

import { NamedInput } from "components/NamedInput/NamedInput";
import { NamedSelect } from "components/NamedSelect/NamedSelect";
import { Option } from "components/Select/Select";
import { Divider } from "components/Divider/Divider";
import { VerticalSpace } from "components/VerticalSpace/VerticalSpace";

import {
  openedDocItemStateAtom,
  openedDocumentAtom,
} from "../../../atoms/documentsAtom";
import { allItemsAtom } from "../../../atoms/itemAtom";
import { measureUnitsAtom } from "../../../atoms/measureUnitsAtom";
import { allPriceListsAtom } from "../../../atoms/priceListAtom";
import { settingsAtom } from "../../../atoms/settingsAtom";

import styles from "../styles.module.scss";
interface ComponentProps {}

const ElementContainer = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  return <div className={styles.componentContainer}>{children}</div>;
};

export const DocItemModal5: FC<ComponentProps> = ({}): JSX.Element => {
  const [allItems] = useAtom(allItemsAtom);
  const [measureUnits] = useAtom(measureUnitsAtom);
  const [allPriceLists] = useAtom(allPriceListsAtom);
  const [openedDocItemState, setOpenedDocumentState] = useAtom(
    openedDocItemStateAtom
  );
  const [openedDocument] = useAtom(openedDocumentAtom);

  const itemsOptions: Option[] = useMemo(
    () =>
      allItems.map((i) => ({ value: i.itemid, label: i.longname || i.name })),
    [allItems]
  );

  const measureUnitsOptions: Option[] = useMemo(
    () => measureUnits.map((i) => ({ value: i.measureid, label: i.name })),
    [measureUnits]
  );

  const priceListsOptions: Option[] = useMemo(
    () => allPriceLists.map((i) => ({ value: i.pricelistid, label: i.name })),
    [allPriceLists]
  );

  const itemVal: Option | undefined = useMemo(
    () =>
      itemsOptions.find((i) => +i.value === Number(openedDocItemState.itemid)),
    [itemsOptions, openedDocItemState.itemid]
  );

  const measureUnitVal: Option | undefined = useMemo(
    () =>
      measureUnitsOptions.find(
        (i) => +i.value === Number(openedDocItemState.measureid)
      ),
    [measureUnitsOptions, openedDocItemState.measureid]
  );

  const priceListVal: Option | undefined = useMemo(
    () =>
      priceListsOptions.find(
        (i) => +i.value === Number(openedDocItemState.pricelistid)
      ),
    [priceListsOptions, openedDocItemState.pricelistid]
  );

  const handleItem = (val) => {
    const selectedItem = allItems.find((i) => i.itemid === val?.value);

    setOpenedDocumentState({
      itemid: val?.value,
      measureid: selectedItem?.measureid || null,
      pricelistid: openedDocument.store?.pricelistid || null,
    } as any);

    if (selectedItem?.prices?.length) {
      const selectedPrice = selectedItem?.prices.find(
        (i) => i.pricelistid === openedDocument.store?.pricelistid
      );

      if (selectedPrice) {
        handleFrontPrice({ target: { value: selectedPrice.price } });
        /*setOpenedDocumentState({
          price: selectedPrice.price,
          salepercent: "0.00",
          amount: 0,
          total: 0,
        } as any);*/
      }
    }
  };

  const handlePrice = (ev) => {
    const objToUpdate: any = { price: ev.target.value };

    objToUpdate.total = String(
      (Number(ev.target.value) * Number(openedDocItemState.amount)).toFixed(2)
    );

    /*objToUpdate.frontprice = String(
      (
        Number(ev.target.value) +
        Number(ev.target.value) * (Number(openedDocItemState.salepercent) / 100)
      ).toFixed(2)
    );*/

    objToUpdate.salepercent = openedDocItemState.frontprice
      ? String(
          (
            ((Number(openedDocItemState.frontprice) - Number(ev.target.value)) /
              Number(ev.target.value)) *
            100
          ).toFixed(0)
        )
      : "";

    setOpenedDocumentState(objToUpdate as any);
  };

  const handleAmount = (ev) => {
    const objToUpdate: any = { amount: ev.target.value };

    objToUpdate.total = String(
      (Number(ev.target.value) * Number(openedDocItemState.price)).toFixed(2)
    );

    setOpenedDocumentState(objToUpdate as any);
  };

  const handleTotal = (ev) => {
    const objToUpdate: any = { total: ev.target.value };

    handlePrice({
      target: {
        value: String(
          (Number(ev.target.value) / Number(openedDocItemState.amount)).toFixed(
            2
          )
        ),
      },
    });

    setOpenedDocumentState(objToUpdate as any);
  };

  const handleSalePercent = (ev) => {
    const objToUpdate: any = { salepercent: ev.target.value };

    objToUpdate.frontprice = String(
      (
        Number(openedDocItemState.price) +
        Number(openedDocItemState.price) * (Number(ev.target.value) / 100)
      ).toFixed(2)
    );

    setOpenedDocumentState(objToUpdate as any);
  };

  const handleFrontPrice = (ev) => {
    const objToUpdate: any = { frontprice: ev.target.value };

    objToUpdate.salepercent = openedDocItemState.price
      ? String(
          (
            ((Number(ev.target.value) - Number(openedDocItemState.price)) /
              Number(openedDocItemState.price)) *
            100
          ).toFixed(0)
        )
      : "";

    setOpenedDocumentState(objToUpdate as any);
  };

  return (
    <div className={styles.wrapper}>
      <ElementContainer>
        <NamedSelect
          options={itemsOptions}
          name={"Товар"}
          nameClassName={styles.inputName}
          className={styles.itemSelect}
          value={itemVal}
          onChange={handleItem}
          placeholder={"Выберите товар"}
        />
      </ElementContainer>
      <div className={styles.inputsRow}>
        <ElementContainer>
          <NamedSelect
            options={measureUnitsOptions}
            name={"Ед. изм"}
            nameClassName={styles.inputName}
            value={measureUnitVal}
            onChange={(val) =>
              setOpenedDocumentState({ measureid: val?.value } as any)
            }
            placeholder={"Выберите ед. изм"}
          />
        </ElementContainer>
        <ElementContainer>
          <NamedSelect
            options={priceListsOptions}
            name={"Прайслист"}
            nameClassName={styles.inputName}
            value={priceListVal}
            onChange={(val) =>
              setOpenedDocumentState({ pricelistid: val?.value } as any)
            }
            placeholder={"Выберите прайслист"}
          />
        </ElementContainer>
      </div>
      <VerticalSpace size={40} />
      <Divider />
      <VerticalSpace size={40} />

      <div className={styles.inputsColumns}>
        <div>
          <ElementContainer>
            <NamedInput
              label={"Кол-во"}
              type={"number"}
              labelClassName={classNames(
                styles.inputName,
                styles.inputNameMarginRight
              )}
              isLabelRegular
              value={openedDocItemState.amount || ""}
              onChange={handleAmount}
            />
          </ElementContainer>
          <div className={styles.inputsRowMiddle}>
            <ElementContainer>
              <NamedInput
                label={"Цена"}
                type={"number"}
                labelClassName={classNames(
                  styles.inputName,
                  styles.inputNameMarginRight
                )}
                isLabelRegular
                value={openedDocItemState.price || ""}
                onChange={handlePrice}
              />
            </ElementContainer>
            <ElementContainer>
              <NamedInput
                label={"% наценки"}
                type={"number"}
                labelClassName={classNames(
                  styles.inputName,
                  styles.inputNameMarginRight
                )}
                isLabelRegular
                value={openedDocItemState.salepercent || ""}
                onChange={handleSalePercent}
              />
            </ElementContainer>
            <ElementContainer>
              <NamedInput
                label={"Прод. цена"}
                type={"number"}
                labelClassName={classNames(
                  styles.inputName,
                  styles.inputNameMarginRight
                )}
                isLabelRegular
                value={openedDocItemState.frontprice || ""}
                onChange={handleFrontPrice}
              />
            </ElementContainer>
          </div>

          <ElementContainer>
            <NamedInput
              label={"Итого"}
              type={"number"}
              labelClassName={classNames(
                styles.inputName,
                styles.inputNameMarginRight
              )}
              isLabelRegular
              value={openedDocItemState.total || ""}
              onChange={handleTotal}
            />
          </ElementContainer>
        </div>
        <div></div>
      </div>

      <VerticalSpace size={100} />
    </div>
  );
};
